import React, { useState, useMemo, BaseSyntheticEvent, useEffect, useCallback } from 'react'
import { Nullable } from 'types/global'
import { FormErrors, FormValue, FormValueInput } from 'types/formValue'
import {
  ControllerRenderProps,
  Path,
  // eslint-disable-next-line no-restricted-imports
  useForm as useHookForm,
  useFormContext as useHookFormContext,
  FieldValues,
  UseFormReturn,
  useController,
  UseFormProps,
  Control,
  SubmitHandler, UseFormHandleSubmit,
} from 'react-hook-form'
import { fieldStateToMeta, formFieldToInput, formValidationResult } from 'services/form'
import { IQuickCommitExecutor, IQuickCommitExecutorOptions, useQuickCommit } from 'hooks/useQuickCommit'
import { GraphQLTaggedNode } from 'react-relay'
import { MutationParameters } from 'relay-runtime'
import { ShowSnackbarOptions, useSnackbar } from 'hooks/redux'
import { trackError } from 'services/analytics'
import { useLogger } from 'hooks/logger'
import { getErrorText, objectToError } from 'services/error'
import { useDebouncedCallback } from 'use-debounce'
import { useDebouncedEffect } from './debounce'
import { useDetectChange } from './state'

/**
 * Represents the possible types of field interactions.
 * Use the `typing` type when the user is typing the value, and `selection` when the user is selecting a value
 */
export type FieldInteractionType = 'typing' | 'selection'

export function useBlurActiveInputElement() {
  const logger = useLogger('useBlurActiveInputElement')

  return useCallback(() => {
    try {
      logger.debug('Looking up active element to blur it...')

      if (document.activeElement instanceof HTMLElement && typeof document.activeElement.blur === 'function') {
        logger.debug('Found blurrable active element')

        if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
          logger.debug('Active element is an input or textarea, blurring it')
          document.activeElement.blur()
        } else {
          logger.debug('Active element is not an input or textarea, skipping blur')
        }
      } else {
        logger.debug('No valid element focused, skipping blur')
      }
    } catch (error) {
      trackError(error, 'high')
    }
  }, [logger])
}

/**
 * Returns a structure with an input and meta object. Used in component tests to simulate redux-form style field data
 *
 * @param name name of the field
 * @param initialValue initial value (optional)
 */
export function useFormValue<T>(name: string, initialValue: Nullable<T> = null) {
  const [value, setValueRaw] = useState<Nullable<T>>(initialValue)
  const [touched, setTouched] = useState(false)
  const setValue = useCallback((val: T) => {
    setValueRaw(val)
    setTouched(true)
  }, [])
  return {
    input: {
      onChange: e => setValue(e?.target ? e.target.value : e),
      value,
      name,
    },
    meta: {
      error: null,
      touched,
    },
  } as FormValue<T>
}

/**
 * Convert `field` data from react-hook-form to a FormValueInput
 *
 * @param field data from react-hook-form's control function
 * @param fieldInteractionType set this to how the user will interact with the field. If the user is typing, set it to
 * 'typing', if the user is selecting a value, set it to 'selection'.
 */
export function useFormInputFromField<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  field: ControllerRenderProps<TFieldValues, TName>,
  fieldInteractionType: FieldInteractionType,
): FormValueInput<any/* PathValue<TFieldValues, TName> */> { // TODO: figure out why types get messed up without any cast
  const logger = useLogger('useFormInputFromField', field.name)
  // In case fieldInteractionType is not passed as a valid value, default to forcing sync changes
  const forceSyncChanges = fieldInteractionType !== 'typing'

  const input = useMemo(() => (
    formFieldToInput<TFieldValues, TName>(field)
  ), [field?.value, field?.name, field?.onBlur, field?.onChange]) // eslint-disable-line react-hooks/exhaustive-deps

  // use temporary state to optimize form updates
  const [tempValue, setTempValue] = React.useState(field.value)

  const debouncedState = useDebouncedEffect(() => {
    if (!forceSyncChanges && input.value !== tempValue) {
      logger.debug('syncing changes to form for field', field.name || 'N/A')
      input.onChange(tempValue)
    }
  }, 800, [input, forceSyncChanges, tempValue], {
    trailing: true, // running the callback on the trailing end of the debouce is the default
  })

  React.useEffect(() => {
    // logger.debug([
    //   'Checking if input value should be written into temp value',
    //   `Temp value: ${tempValue || '(blank)'}`,
    //   `Input value: ${input.value || '(blank)'}`,
    //   `Input value equals temp value: ${input.value === tempValue ? 'yes' : 'no'}`,
    //   `Pending debounced state: ${debouncedState.isPending() ? 'yes' : 'no'}`,
    //   `forceSyncChanges: ${forceSyncChanges ? 'yes' : 'no'}`,
    // ].join(' - '))

    if (!forceSyncChanges && input.value !== tempValue) {
      // logger.debug('Input value change is being synced into temp value')
      setTempValue(input.value)
    } else {
      // logger.debug('Input value change is being ignored')
    }
    // intentionally skip the 'tempValue' dependency here, as the data sync should not go both ways
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, forceSyncChanges])

  const handleChange = React.useCallback((eventOrValue: React.SyntheticEvent<any> | any) => {
    logger.debug('setting temp value for field due to event', field.name || 'N/A')
    if (eventOrValue?.nativeEvent && eventOrValue.nativeEvent instanceof Event) {
      setTempValue(eventOrValue.target?.value)
    } else {
      setTempValue(eventOrValue)
    }
  }, [])

  const handleBlur = React.useCallback((e: React.FocusEvent<any>) => {
    if (!forceSyncChanges && input.value !== tempValue) {
      logger.debug('setting temp value for field due to blur', field.name || 'N/A')
      input.onChange(tempValue)
    }

    input?.onBlur?.(e)
  }, [input, tempValue, forceSyncChanges])

  return useMemo(() => {
    if (forceSyncChanges) {
      return input
    }

    return {
      ...input,
      value: tempValue,
      onChange: handleChange,
      onBlur: handleBlur,
    }
  }, [forceSyncChanges, handleChange, handleBlur, tempValue, input])
}

/**
 * Connect a field to a form and return the input and meta props
 *
 * @param control the control prop coming from useForm
 * @param name name of this field
 * @param fieldInteractionType set this to how the user will interact with the field. If the user is typing, set it to
 * 'typing', if the user is selecting a value, set it to 'selection'.
 */
export function useFormField<TFieldValues extends FieldValues, TContext = any>(control: Control<TFieldValues, TContext>, name: Path<TFieldValues>, fieldInteractionType: FieldInteractionType) {
  const { field, fieldState } = useController<TFieldValues>({ control, name })
  const input = useFormInputFromField(field, fieldInteractionType)

  return useMemo(() => {
    return {
      input,
      meta: fieldStateToMeta(fieldState),
      ref: field.ref,
    }
  }, [field?.ref, fieldState, input])
}

export function useFormFieldFromContext<TFieldValues extends FieldValues>(name: Path<TFieldValues>, fieldInteractionType: FieldInteractionType) {
  // TODO: useHookFormContext causes a re-render when any of the form fields change
  // one way to avoid this is reading from the context directly (FormContext._currentValue), and then syncing it to local state
  // but it's fishy
  //
  // another approach is revisiting the container pattern, probably though the use of some standardized HOC
  // this is also the one hinted at the official docs: https://react-hook-form.com/advanced-usage#FormProviderPerformance
  // but that introduces a lot of boilerplate code

  const { control } = useHookFormContext<TFieldValues>()
  return useFormField(control, name, fieldInteractionType)
}

export function useFormInputFromContext<TFieldValues>(name: Path<TFieldValues>, fieldInteractionType: FieldInteractionType) {
  const field = useFormFieldFromContext(name, fieldInteractionType)

  return field.input
}

export type UseFormReturnCustomized<TFieldValues extends FieldValues, TContext, AdditionalSubmitData> = UseFormReturn<TFieldValues, TContext> & {
  disableSubmit: boolean
  useFormField: (name: Path<TFieldValues>, fieldInteractionType: FieldInteractionType) => ReturnType<typeof useFormField>
  submit: (e?: React.BaseSyntheticEvent, additionalData?: AdditionalSubmitData) => Promise<void>
  submitError: string | null
  handleSubmit: (callback?: SubmitHandler<TFieldValues>) => (e?: React.BaseSyntheticEvent) => Promise<void>
}

export type UseFormPropsCustomized<TFieldValues extends FieldValues, Mutation extends MutationParameters, TContext = {}> = UseFormProps<TFieldValues, TContext> & {
  /**
   * Pass submitHandlers and useForm will return a handy `submit` function that you can call when the form should be submitted, without having to pass any parameters
   */
  submitHandlers?: FormSubmitHandlers<TFieldValues, Mutation>
  /**
   * Set to true to make the form reinitialize automatically when default values change.
   * Make sure to properly memoize default values to prevent a reinitialization on every re-render
   */
  enableReinitialize?: boolean
  /**
   * If true, the form will keep dirty values on reinitializations performed via enableReinitialize
   */
  keepDirtyOnReinitialize?: boolean
}

/**
 * Initialize a react-hook-form with our customized settings
 *
 * @param props options and settings for react-hook-form
 */
export function useForm<TFieldValues extends FieldValues, Mutation extends MutationParameters = any, TContext = {}, AdditionalSubmitData = any>(
  props: UseFormPropsCustomized<TFieldValues, Mutation, TContext>,
): UseFormReturnCustomized<TFieldValues, TContext, AdditionalSubmitData> {
  const logger = useLogger('useForm')

  // Extract custom props, pass the rest to useForm
  const { submitHandlers, enableReinitialize, keepDirtyOnReinitialize, defaultValues, ...useFormProps } = props

  // Handle some additional state
  // Hold the last submit error thrown by the mutation
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Initialize the form data from react-hook-form
  const form = useHookForm<TFieldValues, TContext>({
    mode: 'onTouched', // run validations on blur, and then when an error pops up validate on every change
    defaultValues,
    ...useFormProps,
  })

  const reinitializeOnDefaultValueChange = useMemo(() => enableReinitialize, [])
  const keepDirty = useMemo(() => keepDirtyOnReinitialize, [])

  useEffect(() => {
    if (reinitializeOnDefaultValueChange) {
      // TODO: fix any usage here
      form.reset(defaultValues as TFieldValues, {
        keepDirty,
      })
    }
  }, [reinitializeOnDefaultValueChange, keepDirty, defaultValues])

  // Prepare a curried version of useFormField for less boilerplate, to be exposed in the return value
  const useFormFieldCurried = useCallback(
    // TODO: possibly remove useFormFieldCurried, or make sure it doesn't cause issues (see suppressed eslint warn)

    (name: Path<TFieldValues>, fieldInteractionType: FieldInteractionType) => (
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useFormField<TFieldValues, TContext>(form.control, name, fieldInteractionType)
    ),
    [useFormField, form.control],
  )

  const blurActiveInputElement = useBlurActiveInputElement()

  // Prepare a handy submit function with some extra functionality
  const submit = useMemo(() => {
    // The submit function can only work if submit handlers have been passed
    if (submitHandlers) {
      const [onValidOriginal, onFailedOriginal] = submitHandlers

      // Return a pre-configured function that will submit the form
      return (event?: BaseSyntheticEvent, additionalData?: AdditionalSubmitData) => {
        try {
          // Must prevent default to avoid the HTML form doing weird stuff on submit
          event?.preventDefault()

          // Blur the active element on the page. This is used in case the active element is an input that hasn't
          // committed recent changes by the user to the form data yet, those inputs are supposed to always commit on
          // blur so by blurring them we make sure the form data is up to date.
          blurActiveInputElement()

          // when using the "onValid" callback (the form has no validation errors)
          // record the error from the mutation, if any error was there
          const onValid = async (values: TFieldValues) => {
            setSubmitError(null)

            const result = await onValidOriginal(values, additionalData)
            logger.debug('onValidOriginal has returned the following result', result)

            if (result?.success) {
              logger.debug('onValidOriginal has returned success=true')
            } else {
              logger.debug('onValidOriginal has NOT returned success=true')
              const error = getErrorText(result?.error)
              logger.debug('setting submitError to', error)
              setSubmitError(error)
            }
          }

          const onFailed = () => {
            onFailedOriginal(additionalData)
          }

          const submitHandler = form.handleSubmit(
            onValid,
            onFailed,
          )

          return submitHandler()
        } catch (error) {
          trackError(error, 'high')
        }

        return null
      }
    }

    return () => {
      throw new Error('Cannot submit without submitHandlers')
    }
  }, [submitHandlers, blurActiveInputElement])

  const disableSubmit = useMemo(() => form.formState.isSubmitting || form.formState.isValidating, [form.formState])

  const handleSubmit: UseFormHandleSubmit<TFieldValues, TFieldValues> = useCallback((callback?: SubmitHandler<TFieldValues>) => {
    return form.handleSubmit(callback || (() => {}))
  }, [form.handleSubmit])

  return useMemo<UseFormReturnCustomized<TFieldValues, TContext, AdditionalSubmitData>>(() => {
    return {
      ...form as any, // TODO: fix any usage here
      // Additional read only fields for easier form development
      disableSubmit,
      // pre-configured useFormField to quickly attach fields to this form
      useFormField: useFormFieldCurried,
      // pre-configured function to submit the form, no arguments needed
      submit,
      // the last submit error, if any. Gets automatically cleared on re-submit
      submitError,
      // pre-configured handleSubmit to allow no args
      handleSubmit,
    }
  }, [form, disableSubmit, useFormFieldCurried, submit, submitError])
}

export function useFormContext<
  TFieldValues extends FieldValues,
  TContext = {},
  AdditionalSubmitData = any
>(): UseFormReturnCustomized<TFieldValues, TContext, AdditionalSubmitData> {
  return useHookFormContext<TFieldValues>() as UseFormReturnCustomized<TFieldValues, TContext, AdditionalSubmitData>
}

/**
 * Turn a validator function into a resolver for the form, so it can be used to validate
 * TODO: this doesn't need to be a hook, it can be a regular fn
 *
 * @param validate a sync or async function that returns a simple error structure
 */
export function useFormValidator<TFieldValues extends FieldValues, TContext = {}>(
  validate: (values: TFieldValues, context: TContext) => FormErrors<TFieldValues> | Promise<FormErrors<TFieldValues>>,
) {
  // No need to cache/memo the validation result, react-hook-form already does it
  return useCallback(async (values: TFieldValues, context: TContext) => {
    // react-hook-form supports async validator, so let's keep the support
    return formValidationResult(values, await validate(values, context))
  }, [validate])
}

export interface SubmitMutationResult<T extends MutationParameters> {
  success: boolean
  error: Error
  result: T['response']
}

export interface SubmitResult {
  success: boolean
  error: Error
}

export type FormSubmitHandlers<TFieldValues extends FieldValues, Mutation extends MutationParameters, TAdditionalData = any> = [
  /**
   * The successHandler is called when the form is valid and should perform a mutation to save
   */
  successHandler: (values: TFieldValues, additionalData?: TAdditionalData) => Promise<SubmitMutationResult<Mutation> | SubmitResult>,
  /**
   * The failureHandler is called in case the form is invalid. It can be used to customize what happens when
   * a user tries to submit an invalid form.
   */
  failureHandler: (additionalData?: TAdditionalData) => void
]

/**
 * Returns a submit handler for the form validation failure case, which displays a snackbar message saying to
 * resolve the errors in the form to submit.
 *
 * @param validationErrorMessage customize the message written via snackbar in case of validation failure,
 * or pass null to disable it
 */
export function useSubmitErrorHandler(validationErrorMessage?: string) {
  const { showSnackbarMessage } = useSnackbar()
  return useCallback(() => {
    const message = validationErrorMessage ?? 'Please resolve errors in the form before continuing'
    if (message) showSnackbarMessage(message, { level: 'warning' })
  }, [validationErrorMessage])
}

export function useCallbackBasedFormSubmitHandlers<TFieldValues extends FieldValues, TAdditionalData = any>(
  callback: (data: TFieldValues, additionalData?: TAdditionalData) => void | Promise<void>,
  options?: {
    /**
     * Message to write via snackbar in case of success, or pass null to disable it
     */
    successMessage: string
    /**
     * Customize the message written via snackbar in case of validation failure, or pass null to disable it
     */
    validationErrorMessage?: string
  },
): FormSubmitHandlers<TFieldValues, any> {
  const { showSnackbarMessage } = useSnackbar()
  const submitErrorHandler = useSubmitErrorHandler(options?.validationErrorMessage)

  const successHandler = useCallback(async (data: TFieldValues, additionalData: TAdditionalData) => {
    let error: Error | null = null
    let success = true

    try {
      await callback(data, additionalData)
    } catch (err) {
      error = err as Error
      success = false
    }

    if (success) {
      if (typeof options?.successMessage === 'string') {
        showSnackbarMessage(options?.successMessage, { level: 'success' })
      }
    }

    return {
      success,
      result: null, // TODO: this is required for typings, but should not be ideally
      error: error as Error,
    }
  }, [callback, options?.successMessage, showSnackbarMessage])

  return useMemo(() => [successHandler, submitErrorHandler], [successHandler, submitErrorHandler])
}

export type SuccessMessageProps = string | {
  message: string
  options: ShowSnackbarOptions
}

/**
 * Prepare submit handlers to pass to useForm to handle submitting the form as a GraphQL mutation.
 *
 * @param gql the mutation definition
 * @param valuesToVariables function to convert form values to mutation variables
 * @param options other settings
 */
export function useMutationBasedFormSubmitHandlers<TFieldValues extends FieldValues, T extends MutationParameters, TAdditionalData = any>(
  gql: GraphQLTaggedNode,
  valuesToVariables: (values: TFieldValues, additionalData?: TAdditionalData) => T['variables'] | Promise<T['variables']>,
  options: Omit<IQuickCommitExecutorOptions<T>, 'throwErrors' | 'trackErrors'> & {
    /**
     * This function is used to understand if the mutation was successful. In case of mutation errors, it is
     * automatically considered not successful
     *
     * @param result the mutation execution result, or null if there was an error
     * @param error the mutation execution error
     */
    verifySuccess: (result: T['response'], error: IQuickCommitExecutor<T>['error']) => boolean | Promise<boolean>
    /**
     * Message to write via snackbar in case of success, or pass null to disable it
     */
    successMessage: string | ((result: T['response'], error: IQuickCommitExecutor<T>['error'] | null) => SuccessMessageProps | Promise<SuccessMessageProps>)
    /**
     * Customize the message written via snackbar in case of validation failure, or pass null to disable it
     */
    validationErrorMessage?: string
    /**
     * Provide a callback what will be executed after a successful mutation execution
     */
    onSuccess?: (result: T['response'], error: IQuickCommitExecutor<T>['error'] | null, additionalData?: TAdditionalData) => void | Promise<void>
    /**
     * Provide a callback what will be executed after a failed mutation execution
     */
    onError?: (result: T['response'] | null, error: IQuickCommitExecutor<T>['error'], additionalData?: TAdditionalData) => void | Promise<void>
  },
): FormSubmitHandlers<TFieldValues, T> {
  const logger = useLogger('useMutationBasedFormSubmitHandlers')
  const { showSnackbarMessage } = useSnackbar()
  const mutation = useQuickCommit<T>(gql)

  const submitHandler = useCallback(async (values: TFieldValues, additionalData?: TAdditionalData) => {
    let success: boolean
    let result: T['response'] | null = null
    let error: Error | null = null

    try {
      result = await mutation.execute(await valuesToVariables(values, additionalData), {
        ...options,
        throwErrors: true,
        trackErrors: false,
      })
      // TODO: mutation.error seems to not be available here
      // We probably need to update execute() so it returns the error as well, not only the result.
      if (result) {
        success = typeof options?.verifySuccess === 'function' ?
          await options.verifySuccess(result, mutation.error) :
          !mutation.error
      } else {
        success = false
      }
      error = objectToError(mutation.error)
    } catch (err) {
      logger.error('caught error while executing submit mutation', err)
      success = false
      error = err as Error
    }

    if (success && result) {
      if (typeof options?.successMessage === 'string') {
        showSnackbarMessage(options?.successMessage, { level: 'success' })
      } else if (typeof options?.successMessage === 'function') {
        const successMessageOptions = await options?.successMessage(result, error)

        if (typeof successMessageOptions === 'string') {
          showSnackbarMessage(successMessageOptions, { level: 'success' })
        } else {
          showSnackbarMessage(successMessageOptions.message, successMessageOptions.options)
        }
      }

      if (typeof options?.onSuccess === 'function') {
        await options?.onSuccess(result, error, additionalData)
      }
    } else if (error) {
      if (typeof options?.onError === 'function') {
        await options?.onError(result, error, additionalData)
      }
    }

    return {
      success,
      result,
      error: error as Error,
    }
  }, [mutation?.execute, options])

  const submitErrorHandler = useSubmitErrorHandler(options?.validationErrorMessage)
  return useMemo(() => [submitHandler, submitErrorHandler], [submitHandler, submitErrorHandler])
}

interface IUseFormAutosaveProps<TFieldValues extends FieldValues, TContext = {}, AdditionalSubmitData = any> {
  /**
  * Initial values of the form, used to avoid an autosave to be triggered on mount
  */
  form: UseFormReturnCustomized<TFieldValues, TContext, AdditionalSubmitData>
  disabled?: boolean
  /**
  * Custom submit function. If not provided, calls form.submit
  */
  submit?: () => void | Promise<void>
  /**
  * Values to monitor to trigger the autosave. If not provided, monitors the whole form.
  */
  valuesToWatch?: TFieldValues | string | number | boolean
  /**
  * How long to wait to debounce saves
  */
  debounceSaveCallbackTimeout?: number
}

export function useFormAutosave<TFieldValues extends FieldValues, TContext = {}, AdditionalSubmitData = any>({
  form,
  disabled = false,
  submit,
  valuesToWatch,
  debounceSaveCallbackTimeout = 0,
}:
   IUseFormAutosaveProps<TFieldValues, TContext, AdditionalSubmitData>) {
  const { formState, submit: submitForm } = form

  const saveWithDebounce = useDebouncedCallback(async () => {
    if (!disabled && formState.isDirty && !formState.isSubmitting) {
      await (submit ? submit() : submitForm())
    }
  }, debounceSaveCallbackTimeout, { trailing: true })

  const formValues = form.watch()
  const watchedData = useMemo(() => (
    disabled ? null : (valuesToWatch ?? formValues)
  ), [valuesToWatch, formValues, disabled])

  useDetectChange(watchedData, debounceSaveCallbackTimeout / 2, saveWithDebounce)

  return { saveWithDebounce }
}
